/**
 * Lazy-load category modules on demand to reduce memory usage.
 * Categories are loaded only when first accessed (e.g. user visits /software).
 */
const CATEGORY_MODULES = {
  software: () => import('./software').then((m) => m.software),
  consulting: () => import('./consulting').then((m) => m.consulting),
  gallery: () => import('./gallery').then((m) => m.gallery),
  publications: () => import('./publications').then((m) => m.publications),
  academics: () => import('./academics').then((m) => m.academics),
  crafts: () => import('./crafts').then((m) => m.crafts),
  hospitality: () => import('./hospitality').then((m) => m.hospitality),
  concierge: () => import('./concierge').then((m) => m.concierge),
  coaching: () => import('./coaching').then((m) => m.coaching),
  'supply-chain': () => import('./supplyChain').then((m) => m.supplyChain),
  'family-friends': () => import('./familyFriends').then((m) => m.familyFriends),
};

const cache = new Map();

/**
 * Load items for a category. Returns cached data if already loaded.
 * @param {string} category - Category key (e.g. 'software', 'gallery')
 * @returns {Promise<Array>} Items array for the category
 */
export async function loadCategory(category) {
  if (cache.has(category)) {
    return cache.get(category);
  }
  const loader = CATEGORY_MODULES[category];
  if (!loader) {
    return [];
  }
  const items = await loader();
  cache.set(category, items);
  return items;
}

/**
 * Get category from item id (id / 1000).
 * @param {number|string} id - Item id
 * @returns {string} Category key
 */
export function getCategoryFromId(id) {
  const cat = parseInt(id, 10) / 1000;
  const map = {
    10: 'supply-chain',
    11: 'family-friends',
    9: 'coaching',
    8: 'concierge',
    7: 'hospitality',
    6: 'crafts',
    5: 'academics',
    4: 'publications',
    3: 'gallery',
    2: 'consulting',
    1: 'software',
  };
  return map[Math.floor(cat)] || 'coaching';
}

/**
 * Get all category keys.
 */
export function getCategoryKeys() {
  return Object.keys(CATEGORY_MODULES);
}
